// ── What this contestant has done with each problem (`EX-PUB14`, `EX-PUB15`) ──
// Two surfaces ask the same question: the workspace bar colours its letters with
// it, and the contest page's problem list puts it at the end of each row. One
// definition, because the two answering differently would be a contestant seeing
// A solved in the bar and untouched in the list.
//
// **Their own submissions and nothing else.** A contest problem index carries no
// per-viewer state (`docs/29` §5), and asking the scoreboard would put the
// heaviest read on the platform behind a decoration (`EX-CAP01`). Both callers
// already hold this list, because it is the workspace's verdict poll.

import type { ContestSubmissionItem } from "../types";

export type ContestLetterState = "solved" | "attempted" | "untouched";

/**
 * Fold a contestant's own submissions into one state per letter.
 *
 * "Solved" is a verdict of `accepted` rather than a full score, because it is
 * the one word that means the same thing in `icpc`, `points` and `codeforces`.
 * A later wrong answer never unsolves a problem: the fold takes the best outcome
 * the list holds, exactly as the scoreboard does.
 */
export function buildLetterStates(
  items: readonly ContestSubmissionItem[],
): Map<string, ContestLetterState> {
  const states = new Map<string, ContestLetterState>();
  for (const item of items) {
    if (!item.letter) continue;
    const key = item.letter.toUpperCase();
    if (item.verdict === "accepted") states.set(key, "solved");
    else if (states.get(key) !== "solved") states.set(key, "attempted");
  }
  return states;
}

export function letterStateOf(
  states: Map<string, ContestLetterState>,
  letter: string,
): ContestLetterState {
  return states.get(letter.toUpperCase()) ?? "untouched";
}
